import React, { useState } from 'react';
import { Box, Popover, SxProps, Theme } from '@mui/material';
import CalendarIcon from '@/public/icons/calendar.svg';
import LeaveCalendar from './LeaveCalendar';

type Props = {
  /** 'YYYY-MM-DD' */
  value: string;
  onChange: (date: string) => void;
  /** 'YYYY-MM-DD' — 이 날짜 이전은 선택 불가 */
  minDate?: string;
  /** 'YYYY-MM-DD' — 이 날짜 이후는 선택 불가 */
  maxDate?: string;
  /** placeholder (값이 비었을 때 표시) */
  placeholder?: string;
  sx?: SxProps<Theme>;
};

/**
 * 연차 신청의 LeaveCalendar 를 팝업(Popover)으로 띄우는 단일 날짜 선택 필드.
 * 기존 MUI DatePicker 와 동일한 외형(44px, readonly 필드 + 캘린더 아이콘)을 유지하되,
 * 펼쳐지는 달력만 LeaveCalendar(single 모드)로 대체한다.
 */
const LeaveDatePicker = ({
  value,
  onChange,
  minDate,
  maxDate,
  placeholder,
  sx,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          width: '100%',
          height: '44px',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          padding: '0 16px',
          background: 'var(--dk-bg, #fff)',
          border: `1px solid ${
            open ? 'var(--dk-text, #000)' : 'var(--dk-line, #E7E9EC)'
          }`,
          borderRadius: '8px',
          cursor: 'pointer',
          fontFamily: 'inherit',
          ...sx,
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '20px',
            color: value
              ? 'var(--dk-text, #000)'
              : 'var(--dk-text-dim, #B7BBC2)',
            userSelect: 'none',
          }}
        >
          {value || placeholder || ''}
        </Box>
        <CalendarIcon width={20} height={20} />
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: '8px',
              width: '336px',
              borderRadius: '12px',
              border: '1px solid var(--dk-line, #E7E9EC)',
              boxShadow: '0 16px 32px rgba(0, 0, 0, 0.08)',
            },
          },
        }}
      >
        <LeaveCalendar
          mode="single"
          minDate={minDate}
          maxDate={maxDate}
          startDate={value}
          endDate={value}
          onChange={(start) => {
            onChange(start);
            setAnchorEl(null);
          }}
        />
      </Popover>
    </>
  );
};

export default LeaveDatePicker;
